import { create } from "zustand";
import { Game, RosterEntry } from "@/types";
import { buildAffiliateLink } from "@/lib/links";
import { useAccountStore } from "@/store/account-store";
import { getFirebaseServices } from "@/lib/firebase/client";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  doc,
  serverTimestamp,
  updateDoc,
  getDoc,
  runTransaction,
} from "firebase/firestore";

interface RosterStore {
  entries: RosterEntry[];
  initialized: boolean;
  initListener: () => () => void;
  addEntry: (game: Game, accountId: string, campaignSlug: string) => Promise<RosterEntry>;
  removeEntry: (slotId: string) => Promise<void>;
  restoreEntry: (slotId: string) => Promise<void>;
  getEntries: (accountId?: string | null) => RosterEntry[];
  totalEarnings: () => number;
}

export const useRosterStore = create<RosterStore>((set, get) => ({
  entries: [],
  initialized: false,
  initListener: () => {
    const services = getFirebaseServices();
    if (!services) {
      console.warn("Firebase not configured, roster store disabled");
      return () => {};
    }

    const { db } = services;
    const accountState = useAccountStore.getState();
    
    // Subscribe to account store to get userId
    const unsubAccountStore = useAccountStore.subscribe((state) => {
      console.log('[RosterStore] Account state changed, userId:', state.userId);
    });

    const userId = accountState.userId;
    
    if (!userId) {
      console.log('[RosterStore] No userId yet, waiting...');
      set({ entries: [], initialized: false });
      
      // Wait for userId to be set
      const checkUserId = setInterval(() => {
        const currentUserId = useAccountStore.getState().userId;
        if (currentUserId) {
          clearInterval(checkUserId);
          console.log('[RosterStore] UserId now available:', currentUserId);
          // Re-initialize with userId
          get().initListener();
          return;
        }
      }, 100);
      
      return () => {
        clearInterval(checkUserId);
        unsubAccountStore();
      };
    }

    console.log('[RosterStore] Initializing with userId:', userId);

    // Query all social accounts for this user
    const accountsQuery = query(
      collection(db, "socialAccounts"),
      where("userId", "==", userId)
    );

    let slotUnsubscribers: (() => void)[] = [];
    const entriesByAccount = new Map<string, RosterEntry[]>();

    const updateEntries = () => {
      const allEntries: RosterEntry[] = [];
      entriesByAccount.forEach((entries) => {
        allEntries.push(...entries);
      });
      console.log('[RosterStore] Total entries across all accounts:', allEntries);
      set({ entries: allEntries, initialized: true });
    };

    const unsub = onSnapshot(accountsQuery, (accountsSnapshot) => {
      console.log('[RosterStore] Accounts snapshot received, count:', accountsSnapshot.docs.length);
      
      // Clean up old slot listeners
      slotUnsubscribers.forEach(u => u());
      slotUnsubscribers = [];
      entriesByAccount.clear();

      if (accountsSnapshot.empty) {
        console.log('[RosterStore] No accounts found');
        set({ entries: [], initialized: true });
        return;
      }

      // Set up listener for each account's slots
      accountsSnapshot.docs.forEach((accountDoc) => {
        const accountId = accountDoc.id;
        console.log('[RosterStore] Setting up listener for account:', accountId);
        const slotsRef = collection(db, `socialAccounts/${accountId}/affiliateSlots`);
        // Fetch both active and archived slots
        const slotsQuery = query(slotsRef, where("status", "in", ["active", "archived"]));

        const slotUnsub = onSnapshot(slotsQuery, async (slotsSnapshot) => {
          console.log(`[RosterStore] Slots snapshot for ${accountId}, count:`, slotsSnapshot.docs.length);
          
          const accountEntries: RosterEntry[] = slotsSnapshot.docs.map((docSnap) => {
            const data = docSnap.data();
            return {
              slotId: docSnap.id,
              accountId,
              gameId: data.gameId,
              affiliateLink: data.affiliateLink,
              startedAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
              totalEarnings: data.totalEarnings || 0,
              status: data.status || "active",
            };
          });

          // Reconcile slot status with game status
          // If a slot is active but its game is archived, archive the slot and decrement slotsUsed
          (async () => {
            try {
              const accountState = useAccountStore.getState();
              const account = accountState.accounts.find((acc) => acc.id === accountId);
              if (!account) return;

              for (const slotDoc of slotsSnapshot.docs) {
                const slotData = slotDoc.data();
                // Only check active slots
                if (slotData.status !== 'active') continue;
                
                const gameId = slotData.gameId;
                if (!gameId) continue;

                try {
                  const gameRef = doc(db, 'games', gameId);
                  const gameSnap = await getDoc(gameRef);
                  
                  if (!gameSnap.exists()) {
                    console.warn(`[RosterStore] Game ${gameId} not found for slot ${slotDoc.id}`);
                    continue;
                  }
                  
                  const gameData = gameSnap.data();
                  
                  // If game is archived, archive the slot and decrement slotsUsed
                  if (gameData?.status === 'archived') {
                    console.log(`[RosterStore] Auto-archiving slot ${slotDoc.id} because game ${gameId} is archived`);
                    
                    // Use transaction to atomically update slot and decrement slotsUsed
                    await runTransaction(db, async (tx) => {
                      const slotRef = doc(db, `socialAccounts/${accountId}/affiliateSlots`, slotDoc.id);
                      const accountRef = doc(db, 'socialAccounts', accountId);
                      
                      const accountSnap = await tx.get(accountRef);
                      if (!accountSnap.exists()) return;
                      
                      const currentSlotsUsed = accountSnap.data()?.slotsUsed ?? 0;
                      const newSlotsUsed = Math.max(0, currentSlotsUsed - 1);
                      
                      // Archive the slot
                      tx.update(slotRef, {
                        status: 'archived',
                        archivedAt: serverTimestamp(),
                      });
                      
                      // Decrement slotsUsed
                      tx.update(accountRef, {
                        slotsUsed: newSlotsUsed,
                      });
                      
                      console.log(`[RosterStore] Archived slot and decremented slotsUsed: ${currentSlotsUsed} → ${newSlotsUsed}`);
                    });
                  }
                } catch (innerErr) {
                  console.warn('[RosterStore] Error reconciling slot', slotDoc.id, innerErr);
                }
              }
            } catch (err) {
              console.warn('[RosterStore] Error in slot reconciliation', err);
            }
          })();

          entriesByAccount.set(accountId, accountEntries);
          console.log(`[RosterStore] Updated entries for account ${accountId}:`, accountEntries);
          updateEntries();
        });

        slotUnsubscribers.push(slotUnsub);
      });
    });

    return () => {
      console.log('[RosterStore] Cleaning up listeners');
      unsub();
      slotUnsubscribers.forEach(u => u());
      unsubAccountStore();
    };
  },
  addEntry: async (game, accountId, campaignSlug) => {
    const accountState = useAccountStore.getState();
    const account = accountState.accounts.find((acc) => acc.id === accountId);
    if (!account) {
      throw new Error("Select a social account before promoting a game.");
    }
    if (account.slotsUsed >= account.slotsLimit) {
      throw new Error("All 3 slots are currently filled for this account.");
    }
    const alreadyPromoting = get().entries.some(
      (entry) => entry.accountId === accountId && entry.gameId === game.id
    );
    if (alreadyPromoting) {
      throw new Error("This game is already in the roster for the selected account.");
    }

    const services = getFirebaseServices();
    if (!services || !accountState.userId) {
      throw new Error("Firebase not configured or user not logged in.");
    }

    const affiliateLink = buildAffiliateLink({
      gameSlug: game.slug,
      campaignSlug,
      accountHandle: account.handle,
    });

    // Write to subcollection: socialAccounts/{accountId}/affiliateSlots
    const slotsRef = collection(services.db, `socialAccounts/${accountId}/affiliateSlots`);
    console.log('[RosterStore] Writing to:', `socialAccounts/${accountId}/affiliateSlots`);
    const docRef = await addDoc(slotsRef, {
      gameId: game.id,
      campaignSlug,
      affiliateLink,
      status: "active",
      createdAt: serverTimestamp(),
      totalEarnings: 0,
    });
    console.log('[RosterStore] Created slot:', docRef.id);

    // Increment slot count
    await accountState.updateAccount(accountId, {
      slotsUsed: account.slotsUsed + 1,
    });

    return {
      slotId: docRef.id,
      accountId,
      gameId: game.id,
      affiliateLink,
      startedAt: new Date().toISOString(),
      totalEarnings: 0,
    };
  },
  removeEntry: async (slotId) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase not configured.");

    const state = get();
    const entry = state.entries.find((item) => item.slotId === slotId);
    if (!entry) return;

    // Update in subcollection: socialAccounts/{accountId}/affiliateSlots/{slotId}
    await updateDoc(doc(services.db, `socialAccounts/${entry.accountId}/affiliateSlots`, slotId), {
      status: "archived",
      archivedAt: serverTimestamp(),
    });

    const accountState = useAccountStore.getState();
    const account = accountState.accounts.find((acc) => acc.id === entry.accountId);
    if (account) {
      await accountState.updateAccount(entry.accountId, {
        slotsUsed: Math.max(0, account.slotsUsed - 1),
      });
    }
  },
  restoreEntry: async (slotId) => {
    const services = getFirebaseServices();
    if (!services) throw new Error("Firebase not configured.");

    const state = get();
    const entry = state.entries.find((item) => item.slotId === slotId);
    if (!entry) return;

    // Update slot back to active
    await updateDoc(doc(services.db, `socialAccounts/${entry.accountId}/affiliateSlots`, slotId), {
      status: "active",
      archivedAt: null,
    });

    // Increment slot count on the account
    const accountState = useAccountStore.getState();
    const account = accountState.accounts.find((acc) => acc.id === entry.accountId);
    if (account) {
      await accountState.updateAccount(entry.accountId, {
        slotsUsed: Math.min(account.slotsLimit, account.slotsUsed + 1),
      });
    }
  },
  getEntries: (accountId) => {
    if (!accountId) return get().entries;
    return get().entries.filter((entry) => entry.accountId === accountId);
  },
  totalEarnings: () =>
    get().entries.reduce((sum, entry) => sum + entry.totalEarnings, 0),
}));
